import React, {
  ChangeEvent,
  FC,
  useRef,
  useState,
  FocusEvent,
  useContext,
  useEffect,
  useCallback,
} from 'react';
import { ValueFieldProps } from '@sitecore-jss/sitecore-jss-react-forms';
import { StringInputViewModel } from '@sitecore-jss/sitecore-jss-forms';
import CustomTextLabel from '../@shared/customTextLabel';
import { CustomFormContext } from '../customFormContextProvider';
import { debounceHandler } from '../../../../utils/debounceHandler.utils';
import HighlightedOption from '../../../@shared/highlightedOption';
import Icon from '../../../@shared/icon';
import { iconSize } from '../../../../constants/icon.constants';
import DictionaryTerm from '../../../@shared/dictionaryTerm';
import { TGooglePlaceAddressSuggestion } from '../../types/addressField.types';
import { APIPrefix } from '../../../../constants/api.constants';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import useFetchPredictions from './hooks/useFetchPredictions';
import usePlaceService from './hooks/usePlaceService';
import { documentService } from '../../../../utils/window.utils.js';
import APIProviderGoogleMap from '../../../@shared/apiProviderGoogleMap';
import './styles.scss';

const CustomAddressFieldComponent: FC<ValueFieldProps<StringInputViewModel>> = (props) => {
  const { field, tracker, value, errors, fieldValidationErrorsComponent } = props;
  const [hasFocus, setHasFocus] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const hasValueOrFocus = hasFocus || inputValue !== '';
  const hasAnyError = !!errors?.length;
  const FieldValidationErrors = fieldValidationErrorsComponent;
  const label = field?.model?.title;
  const id = field?.valueField?.id;
  const name = field?.valueField?.name;
  const placeholder = field?.model?.placeholderText;
  const { sitecoreContext } = useSitecoreContext();
  const itemID = sitecoreContext.route?.itemId;
  const siteLanguage = sitecoreContext?.language;
  const customFormContext = useContext(CustomFormContext);
  const [_, setSelectedAddress] = customFormContext?.selectedAddress ?? [];
  const [autoAddressDisabled, setAutoAddressDisabled] = useState(false);
  const [highlightedSuggestionIndex, setHighlightedSuggestionIndex] = useState<number | null>(null);
  const [suggestions, setSuggestions] = useState<TGooglePlaceAddressSuggestion[]>([]);
  const [countryIsoCodes, setCountryIsoCodes] = useState({ country: [] });

  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const DEBOUNCE_DELAY = 200;

  const addressSearchElmRef = useRef<HTMLDivElement>(null);

  const { getSuggestions } = useFetchPredictions(countryIsoCodes);
  const { getPlaceDetails } = usePlaceService();

  const fetchData = useCallback(async () => {
    const getApiURL = `/${APIPrefix}/countrystate/iso/${itemID}/${siteLanguage}`;
    try {
      const res = await fetch(getApiURL);
      if (!res.ok) {
        throw Error(`${res.status} : Failed to fetch data`);
      }
      const result = await res.json();
      setCountryIsoCodes(result);
    } catch (error) {
      console.error(`${error} occurred while fetching data`);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (inputValue !== value) {
      setInputValue(value);
      props.onChange(field.valueField.name, value, props.isValid, []);
    }
  }, [props.value]);

  const handleFetchPredictions = (inputValue: string) => {
    getSuggestions(inputValue, (results: TGooglePlaceAddressSuggestion[]) => {
      setSuggestions(results);
    });
  };

  const handleSuggestionClick = (
    placeId: string,
    structured_formatting: google.maps.places.StructuredFormatting
  ) => {
    getPlaceDetails(placeId, ({ address }) => {
      if (setSelectedAddress)
        setSelectedAddress({
          ...address,
        });

      setInputValue(structured_formatting?.main_text || '');
      props.onChange(
        field?.valueField?.name,
        structured_formatting?.main_text || '',
        props?.isValid,
        []
      );
      setSuggestions([]);
    });
  };

  const onFocus = (e: FocusEvent<HTMLInputElement>): void => {
    tracker.onBlurField(field, e?.target?.value);
    setHasFocus(true);
  };

  const onBlur = (e: FocusEvent<HTMLInputElement>): void => {
    tracker.onBlurField(field, e?.target?.value);
    setHasFocus(false);
  };

  const handleClickOutside = (e) => {
    if (!autoAddressDisabled) {
      if (addressSearchElmRef?.current?.contains(e?.target)) {
        return;
      }
      setSuggestions([]);
    }
  };

  useEffect(() => {
    documentService().addEventListener('mouseup', handleClickOutside);
    return (): void => {
      documentService().removeEventListener('mouseup', handleClickOutside);
    };
  }, [inputValue]); /* eslint-disable-line */

  const onChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    setInputValue(value);
    if (!autoAddressDisabled && value.trim() !== '') {
      debounceHandler(() => handleFetchPredictions(value), timerRef, DEBOUNCE_DELAY);
    } else {
      props.onChange(field?.valueField?.name, value, props?.isValid, []);
    }
  };

  const handleSuggestionMouseEnter = (index: number): void => {
    setHighlightedSuggestionIndex(index);
  };

  return (
    <>
      <div className="sitecore-jss-form__control--address" ref={addressSearchElmRef}>
        <div
          className={`sitecore-jss-form__control sitecore-jss-form__control--text-box ${
            hasAnyError ? 'sitecore-jss-form__control--has-error' : ''
          }`}
        >
          <label
            htmlFor={id}
            className={`sitecore-jss-form__label ${
              hasValueOrFocus ? 'sitecore-jss-form__label--has-focus' : ''
            }`}
          >
            <CustomTextLabel label={label} />
          </label>
          <input
            id={id}
            name={name}
            className={`sitecore-jss-form__field ${
              inputValue ? 'sitecore-jss-form__field-value' : ''
            }`}
            type="text"
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholder={placeholder}
            value={inputValue}
            form="novalidatedform"
          />

          {suggestions.length > 0 && (
            <ul className="sitecore-jss-form__control--address__text-search-suggestion-group">
              {suggestions.map((result, i: number) => (
                <li
                  className={`sitecore-jss-form__control--address__text-search-suggestions-list-item ${
                    highlightedSuggestionIndex === i
                      ? 'sitecore-jss-form__control--address__text-search-suggestions-list-item--highlighted'
                      : ''
                  }`}
                  onMouseEnter={() => handleSuggestionMouseEnter(i)}
                  onClick={() =>
                    handleSuggestionClick(result.place_id, result.structured_formatting)
                  }
                  key={result.place_id}
                >
                  <HighlightedOption keyword={inputValue} optionText={result?.description} />
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="sitecore-jss-form__control sitecore-jss-form__control--checkboxfield">
          <input
            id="autoAddressFormFiled"
            name={name}
            className="sitecore-jss-form__field"
            type="checkbox"
            onChange={(e) => {
              setAutoAddressDisabled(e.target.checked);
              setSuggestions([]);
            }}
            checked={autoAddressDisabled}
            form="novalidatedform"
          />
          <label
            htmlFor="autoAddressFormFiled"
            className="sitecore-jss-form__control__label--checkbox-label"
          >
            <Icon
              iconName={autoAddressDisabled ? 'action-checkbox-checked' : 'action-checkbox-default'}
              size={iconSize.dsk}
              extraClassName="sitecore-jss-form__checkbox"
            />
            <DictionaryTerm customTextTag="span" keyword="cnhi-auto-address-checkbox-label" />
          </label>
        </div>
      </div>
      {FieldValidationErrors && <FieldValidationErrors {...props} />}
    </>
  );
};

const CustomAddressField: FC<ValueFieldProps<StringInputViewModel>> = (props) => {
  const { field } = props;

  const mapsApiKey = (field?.model?.googleApikey as string) || '';
  return (
    <APIProviderGoogleMap mapApiKey={mapsApiKey} libraries={['places']}>
      <CustomAddressFieldComponent {...props} />
    </APIProviderGoogleMap>
  );
};

export default CustomAddressField;
